import { apiClient } from './apiClient';

/**
 * Wire values for a history event's type. The backend maps database rows to these; the
 * frontend maps them to user-facing titles (see poiHistoryLabels). They never expose
 * table names or internal structure.
 */
export type PoiHistoryEventType = 'poi_assessment' | 'nodig_leak' | 'nodig_repair' | 'nodig_recheck';

/** Details for a `poi_assessment` event — a survey assessment recorded against the POI family. */
export interface PoiAssessmentDetails {
    stop: number;
    /** 0 for the original POI record; 1+ for additional leaks recorded against it. */
    suffix: number;
    /** ISO "yyyy-MM-dd". */
    surveyedDate: string;
    /** ISO "yyyy-MM-dd" or null. */
    searchDate: string | null;
    leakWhatThreeWords: string | null;
    leakageAddress: string | null;
    /** Label: "Comms" | "CSPL" | "Main" | "Fitting" | "STBB" | null. */
    pipeType: string | null;
    leakageRate: number | null;
    isLeakVisible: boolean | null;
    repaired: boolean | null;
    nodigFix: boolean | null;
    /** Label: "Yes" | "No" | "Tried and Failed" | "Unknown" | null. */
    nodigSuitable: string | null;
    longitude: number | null;
    latitude: number | null;
    clientWorkOrderNumber: string | null;
    leakResultReason: string | null;
    leakResultReasonOther: string | null;
    smartMeterFound: boolean | null;
    comments: string | null;
    /** ISO timestamp "yyyy-MM-ddTHH:mm:ss". */
    created: string;
}

/** Details for a `nodig_leak` event — a leak registered in the No Dig programme. */
export interface NoDigLeakDetails {
    /** Label: "Open" | "Fixed" | "Unsuitable" | "Closed". */
    status: string;
    /** Label: "Find & Fix" | "Client Worklist" | "Standalone" | "Legacy". */
    sourceType: string;
    address: string | null;
    whatThreeWords: string | null;
    pipeType: string | null;
    leakageRate: number | null;
    isLeakVisible: boolean | null;
    smartMeterFound: boolean | null;
    alreadyMarkedUp: boolean;
    longitude: number | null;
    latitude: number | null;
    /** ISO timestamp "yyyy-MM-ddTHH:mm:ss". */
    created: string;
}

/** Details for a `nodig_repair` event — a technician intervention on a No Dig leak. */
export interface NoDigRepairDetails {
    applicationNumber: number;
    suitable: boolean | null;
    repaired: boolean | null;
    /** ISO "yyyy-MM-dd" or null. */
    siteVisited: string | null;
    /** ISO "yyyy-MM-dd" or null. */
    repairedDate: string | null;
    mainsPressure: number | null;
    pressureBand: string | null;
    batchName: string | null;
    pipeSide: string | null;
    /** Canonical label, e.g. "Customer side leak", "Other"; null when suitable. */
    unsuitableReason: string | null;
    /** Free text, only populated when the reason is "Other". */
    unsuitableReasonOther: string | null;
    clientWorkOrderNumber: string | null;
    comments: string | null;
    /** ISO timestamp "yyyy-MM-ddTHH:mm:ss". */
    created: string;
}

/** Details for a `nodig_recheck` event — a follow-up recheck on a No Dig leak. */
export interface NoDigRecheckDetails {
    /** Label: "Noise" | "No Noise" | "Inconclusive" | "Digging" | "Unable to Isolate". */
    result: string;
    /** ISO "yyyy-MM-dd" or null. */
    recheckDate: string | null;
    mainsPressure: number | null;
    clientWorkOrderNumber: string | null;
    comments: string | null;
    /** ISO timestamp "yyyy-MM-ddTHH:mm:ss". */
    created: string;
}

/**
 * One event on a POI's history timeline. Exactly one of the detail objects is non-null,
 * matching `eventType` — the timeline renders the summary fields and the detail view
 * renders the matching detail object with no further fetching.
 */
export interface PoiHistoryEvent {
    /** Stable opaque identifier ("{eventType}:{rowGuid}"). */
    id: string;
    eventType: PoiHistoryEventType;
    /** Display date, ISO "yyyy-MM-dd" after the backend's per-type fallback chain, or null. */
    date: string | null;
    technician: string | null;
    /** Server-mapped status/result label (e.g. "Leak Found", "Repaired", "No Noise"). */
    summary: string | null;
    /** ISO timestamp — the backend's deterministic ordering tie-breaker. */
    created: string;
    assessmentDetails: PoiAssessmentDetails | null;
    noDigLeakDetails: NoDigLeakDetails | null;
    repairDetails: NoDigRepairDetails | null;
    recheckDetails: NoDigRecheckDetails | null;
}

export interface PoiHistoryResponse {
    /**
     * The parent POI of the family the requested id belongs to. Requesting any family
     * member (a child leak marker included) returns the same timeline.
     */
    familyPoiId: string;
    /** Events newest-first (server-ordered with a deterministic tie-break). */
    events: PoiHistoryEvent[];
}

/**
 * Fetches the unified history timeline for a POI. Uses GET /pois/{poiId}/history
 * (LeakDetectionModule policy) with the clientId override query param so Origin users
 * can view the currently selected client's data.
 *
 * Event details are embedded in the response — the detail view needs no second request.
 * Pass an AbortSignal so an in-flight request is cancelled when the selected POI changes.
 */
export async function fetchPoiHistory(
    clientId: string,
    poiId: string,
    signal?: AbortSignal,
): Promise<PoiHistoryResponse> {
    const response = await apiClient.get<PoiHistoryResponse>(`/pois/${encodeURIComponent(poiId)}/history`, {
        params: { clientId },
        signal,
    });
    return response.data;
}
